import React from "react";
import { SmilePlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { REACTIONS, REACTION_MAP } from "@/lib/reactions";
import { ReactionHoverPreview } from "@/components/social/reactions/ReactionHoverPreview";
import { cn } from "@/lib/utils";

/**
 * Reusable native-emoji reaction control. Shows a compact "React" trigger that
 * opens an 8-emoji picker, plus one chip per reacted emoji rendered exactly once
 * with its count (e.g. "🤗 3"). The current user's reaction chip is highlighted
 * and clicking any chip toggles that reaction.
 * `reactions` is a { key: count } map; `myReaction` is the current user's key.
 */
export function ReactionBar({ reactions = {}, myReaction, onReact, testId, popoverClassName,
                             target, onSeeReactors }) {
  const chips = REACTIONS.filter((r) => reactions[r.key]);
  // A chip stays a reaction toggle. When a `target` is supplied (Chatter only) we add a
  // hover-only preview on top of it; Event/Resource comments pass no target and are unchanged.
  const withPreview = (r, node) =>
    target ? (
      <ReactionHoverPreview key={r.key} target={target} emojiKey={r.key}
        count={reactions[r.key]} onSeeAll={onSeeReactors}>
        {node}
      </ReactionHoverPreview>
    ) : node;
  return (
    <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 px-1.5 text-muted-foreground hover:text-primary sm:h-7 sm:px-2"
            data-testid={testId ? `${testId}-trigger` : undefined}
          >
            <SmilePlus className="h-4 w-4" />
            {/* The word is the first thing a phone gives up: the icon carries the meaning,
                and dropping it is what lets the whole action row stay on one line. */}
            {chips.length === 0 && (
              <span className="hidden text-xs sm:inline">React</span>
            )}
            {chips.length === 0 && <span className="sr-only">React</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className={cn("w-auto p-1.5", popoverClassName)}>
          <div className="flex gap-0.5">
            {REACTIONS.map((r) => (
              <button
                key={r.key}
                onClick={() => onReact(r.key)}
                title={r.label}
                aria-label={r.label}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-125 ${myReaction === r.key ? "bg-secondary" : ""}`}
                data-testid={testId ? `${testId}-${r.key}` : undefined}
              >
                {r.emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {chips.map((r) => withPreview(r,
        <button
          key={r.key}
          onClick={() => onReact(r.key)}
          title={REACTION_MAP[r.key].label}
          aria-label={`${REACTION_MAP[r.key].label} ${reactions[r.key]} — click to react`}
          className="inline-flex h-7 items-center gap-1 rounded-full bg-secondary/60 px-2 text-xs text-muted-foreground transition-colors hover:bg-secondary"
          data-testid={testId ? `${testId}-count-${r.key}` : undefined}
        >
          <span className="text-sm leading-none">{r.emoji}</span>
          <span className="font-[560]">{reactions[r.key]}</span>
        </button>
      ))}
    </div>
  );
}
